import React, { useState } from 'react';
import { PropertyFilter } from '../api/api';
import { useLanguage } from '../contexts/LanguageContext';

interface SearchFiltersProps {
  filters: PropertyFilter;
  onFilterChange: (filters: PropertyFilter) => void;
}

const SearchFilters: React.FC<SearchFiltersProps> = ({ filters, onFilterChange }) => {
  const { t } = useLanguage();
  const [localFilters, setLocalFilters] = useState<PropertyFilter>(filters);

  const handleChange = (field: keyof PropertyFilter, value: any) => {
    const newFilters = { ...localFilters, [field]: value };
    setLocalFilters(newFilters);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onFilterChange(localFilters);
  };

  const handleReset = () => {
    const resetFilters: PropertyFilter = { page: 1, page_size: 20, status: 'active' };
    setLocalFilters(resetFilters);
    onFilterChange(resetFilters);
  };

  return (
    <form onSubmit={handleSubmit} className="card p-5 sm:p-6">
      <h2 className="text-lg sm:text-xl font-extrabold mb-5 text-gray-900 flex items-center gap-2">
        <span>🔍</span> {t('filters')}
      </h2>

      <div className="space-y-4">
        <div>
          <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
            {t('propertyType')}
          </label>
          <select
            className="input text-sm"
            value={localFilters.property_type || ''}
            onChange={(e) => handleChange('property_type', e.target.value || undefined)}
          >
            <option value="">{t('allTypes')}</option>
            <option value="apartment">{t('apartment')}</option>
            <option value="house">{t('house')}</option>
          </select>
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
            {t('city')}
          </label>
          <input
            type="text"
            className="input text-sm"
            placeholder={t('enterCity')}
            value={localFilters.city || ''}
            onChange={(e) => handleChange('city', e.target.value || undefined)}
          />
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
            {t('price')} ({t('sum')})
          </label>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="number"
              className="input text-sm"
              placeholder={t('from')}
              value={localFilters.min_price || ''}
              onChange={(e) => handleChange('min_price', e.target.value ? Number(e.target.value) : undefined)}
            />
            <input
              type="number"
              className="input text-sm"
              placeholder={t('to')}
              value={localFilters.max_price || ''}
              onChange={(e) => handleChange('max_price', e.target.value ? Number(e.target.value) : undefined)}
            />
          </div>
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
            {t('area')} ({t('sqm')})
          </label>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="number"
              className="input text-sm"
              placeholder={t('from')}
              value={localFilters.min_area || ''}
              onChange={(e) => handleChange('min_area', e.target.value ? Number(e.target.value) : undefined)}
            />
            <input
              type="number"
              className="input text-sm"
              placeholder={t('to')}
              value={localFilters.max_area || ''}
              onChange={(e) => handleChange('max_area', e.target.value ? Number(e.target.value) : undefined)}
            />
          </div>
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
            {t('rooms')}
          </label>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="number"
              className="input text-sm"
              placeholder={t('from')}
              value={localFilters.min_rooms || ''}
              onChange={(e) => handleChange('min_rooms', e.target.value ? Number(e.target.value) : undefined)}
            />
            <input
              type="number"
              className="input text-sm"
              placeholder={t('to')}
              value={localFilters.max_rooms || ''}
              onChange={(e) => handleChange('max_rooms', e.target.value ? Number(e.target.value) : undefined)}
            />
          </div>
        </div>

        <div className="flex gap-2 pt-4 border-t-2 border-gray-100">
          <button type="submit" className="btn btn-primary flex-1 font-bold text-sm py-3 shadow-sm">
            ✓ {t('apply')}
          </button>
          <button type="button" onClick={handleReset} className="btn btn-secondary font-bold text-sm py-3 px-4 shadow-sm">
            ↻
          </button>
        </div>
      </div>
    </form>
  );
};

export default SearchFilters;
